import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  Divider,
  Chip,
} from '@mui/material';
import {
  Assignment as ProjetIcon,
  Build as ServiceIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as ActiveIcon,
} from '@mui/icons-material';
import { GlassBackdrop, GlassSurface } from '../../components/GlassDashboard';
import { projetsAPI, servicesAPI, productionDashboardAPI, ProductionDashboardStats } from '../../services/api';
import { Projet } from '../../types/projet';
import { Service } from '../../types/service';

const ProductionOverview: React.FC = () => {
  const [projetsList, setProjetsList] = useState<Projet[]>([]);
  const [servicesList, setServicesList] = useState<Service[]>([]);
  const [stats, setStats] = useState<ProductionDashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsData, projetsData, servicesData] = await Promise.all([
          productionDashboardAPI.getStats(),
          projetsAPI.getAll(0, 5),
          servicesAPI.getAll(0, 5),
        ]);
        setStats(statsData);
        setProjetsList(projetsData.items || []);
        setServicesList(servicesData.items || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Stats (server-aggregated)
  const totalProjets = stats?.projets.total ?? 0;
  const totalServices = stats?.services.total ?? 0;
  const activeServices = stats?.services.active ?? 0;

  const statsCards = [
    {
      title: 'Total Projets',
      value: totalProjets,
      icon: <ProjetIcon sx={{ fontSize: 40 }} />,
      color: '#FF5722',
    },
    {
      title: 'Total Services',
      value: totalServices,
      icon: <ServiceIcon sx={{ fontSize: 40 }} />,
      color: '#2196F3',
    },
    {
      title: 'Services Actifs',
      value: activeServices,
      icon: <ActiveIcon sx={{ fontSize: 40 }} />,
      color: '#4CAF50',
    },
    {
      title: 'Taux Activité',
      value: totalServices > 0 ? `${Math.round((activeServices / totalServices) * 100)}%` : '0%',
      icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
      color: '#FF9800',
    },
  ];

  return (
    <GlassBackdrop>
      <Typography variant="h4" gutterBottom sx={{ color: '#FF5722', fontWeight: 600 }}>
        Tableau de Bord Production
      </Typography>

      {/* Stats Section */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {statsCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <GlassSurface accentColor={card.color} sx={{ p: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    {card.title}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {loading ? '...' : card.value}
                  </Typography>
                </Box>
                <Box sx={{ color: card.color }}>{card.icon}</Box>
              </Box>
            </GlassSurface>
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <GlassSurface accentColor="#FF5722" sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#FF5722' }}>
              Projets Récents
            </Typography>
            {loading ? (
              <Typography>Chargement...</Typography>
            ) : projetsList.length === 0 ? (
              <Typography color="textSecondary">Aucun projet enregistré</Typography>
            ) : (
              <Box>
                {projetsList.slice(0, 5).map((projet) => (
                  <Box
                    key={projet.id}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 1.5,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {projet.code}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {projet.nom}
                      </Typography>
                    </Box>
                    <Chip
                      label="Actif"
                      size="small"
                      color="success"
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </GlassSurface>
        </Grid>

        <Grid item xs={12} md={6}>
          <GlassSurface accentColor="#FF5722" sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#FF5722' }}>
              Services
            </Typography>
            {loading ? (
              <Typography>Chargement...</Typography>
            ) : servicesList.length === 0 ? (
              <Typography color="textSecondary">Aucun service enregistré</Typography>
            ) : (
              <Box>
                {servicesList.slice(0, 5).map((service) => (
                  <Box
                    key={service.id}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 1.5,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {service.code}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {service.name}
                      </Typography>
                    </Box>
                    <Chip
                      label={service.is_active ? 'Actif' : 'Inactif'}
                      size="small"
                      color={service.is_active ? 'success' : 'default'}
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </GlassSurface>
        </Grid>
      </Grid>
    </GlassBackdrop>
  );
};

export default ProductionOverview;
